/**
 * Memory context component for Clean UI.
 *
 * Updated to use agentApi instead of Flask backend.
 *
 * Blueprint: specs/019-clean-ui-no-api/
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Copy,
  Database,
  Download,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
  User,
  Users,
  Building2,
  Bot,
} from 'lucide-react';
import type {
  MemoryItem,
  MemoryScope,
  MemoryType,
  MemorySource,
} from '../../types/chat';
import { agentApi } from '../../services/agentApi';

interface MemoryContextProps {
  memories: MemoryItem[];
  userId?: string;
  defaultExpanded?: boolean;
}

const SCOPE_CONFIG: Record<
  MemoryScope,
  { icon: React.ElementType; label: string; color: string }
> = {
  user: { icon: User, label: 'Personal', color: 'text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20' },
  team: { icon: Users, label: 'Team', color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' },
  org: {
    icon: Building2,
    label: 'Organization',
    color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
  },
  agent: { icon: Bot, label: 'Agent', color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20' },
};

const TYPE_LABELS: Record<MemoryType, string> = {
  personal: 'Personal',
  task: 'Task',
  tool: 'Tool',
};

const SOURCE_LABELS: Record<MemorySource, string> = {
  agent_recorded: 'Recorded',
  user_explicit: 'You shared',
  inferred: 'Inferred',
};

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const level =
    confidence >= 0.8 ? 'high' : confidence >= 0.5 ? 'medium' : 'low';
  const colors = {
    high: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
    medium: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
    low: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  };
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${colors[level]}`}>
      {Math.round(confidence * 100)}%
    </span>
  );
}

function MemoryItemRow({
  memory,
  isEditing,
  editingText,
  onEditChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onCopy,
  disabled,
}: {
  memory: MemoryItem;
  isEditing: boolean;
  editingText: string;
  onEditChange: (value: string) => void;
  onStartEdit: (memory: MemoryItem) => void;
  onCancelEdit: () => void;
  onSaveEdit: (memory: MemoryItem) => void;
  onDelete: (memory: MemoryItem) => void;
  onCopy: (memory: MemoryItem) => void;
  disabled: boolean;
}) {
  const scopeConfig = SCOPE_CONFIG[memory.scope];
  const ScopeIcon = scopeConfig.icon;

  return (
    <div className="flex items-start gap-2 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <div
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${scopeConfig.color}`}
      >
        <ScopeIcon className="w-3 h-3" />
        <span>{scopeConfig.label}</span>
      </div>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <textarea
            value={editingText}
            onChange={(event) => onEditChange(event.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-100 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            rows={3}
          />
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-snug">{memory.content}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {TYPE_LABELS[memory.memoryType]}
          </span>
          <span className="text-xs text-gray-300 dark:text-gray-600">•</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {SOURCE_LABELS[memory.source]}
          </span>
          {memory.category ? (
            <>
              <span className="text-xs text-gray-300 dark:text-gray-600">•</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{memory.category}</span>
            </>
          ) : null}
          <span className="text-xs text-gray-300 dark:text-gray-600">•</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            rel {memory.relevanceScore.toFixed(2)}
          </span>
          <ConfidenceBadge confidence={memory.confidence} />
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isEditing ? (
          <>
            <button
              type="button"
              onClick={() => onSaveEdit(memory)}
              className="p-1 text-emerald-500 hover:text-emerald-700"
              title="Save edit"
              disabled={disabled}
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              title="Cancel edit"
              disabled={disabled}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onCopy(memory)}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              title="Copy memory"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onStartEdit(memory)}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              title="Edit memory"
              disabled={disabled}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(memory)}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"
              title="Delete memory"
              disabled={disabled}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function MemoryContext({
  memories,
  userId,
  defaultExpanded = false,
}: MemoryContextProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [query, setQuery] = useState('');
  const [activeScopes, setActiveScopes] = useState<MemoryScope[]>([
    'user',
    'team',
    'org',
    'agent',
  ]);
  const [activeTypes, setActiveTypes] = useState<MemoryType[]>([
    'personal',
    'task',
    'tool',
  ]);
  const [sortBy, setSortBy] = useState<'relevance' | 'confidence' | 'scope'>(
    'relevance',
  );
  const [localMemories, setLocalMemories] = useState<MemoryItem[]>(memories);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newScope, setNewScope] = useState<MemoryScope>('user');
  const [newType, setNewType] = useState<MemoryType>('personal');
  const [showCreate, setShowCreate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canManage = Boolean(userId);

  useEffect(() => {
    setLocalMemories(memories);
  }, [memories]);

  useEffect(() => {
    if (!isExpanded) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isExpanded]);

  // These are constant arrays - no need for useMemo
  const scopedFilters = [
    { value: 'user' as const, label: 'Personal' },
    { value: 'team' as const, label: 'Team' },
    { value: 'org' as const, label: 'Org' },
    { value: 'agent' as const, label: 'Agent' },
  ];

  const typeFilters = [
    { value: 'personal' as const, label: 'Personal' },
    { value: 'task' as const, label: 'Task' },
    { value: 'tool' as const, label: 'Tool' },
  ];

  const filteredMemories = useMemo(() => {
    if (!memories || memories.length === 0) return [];
    const queryLower = query.trim().toLowerCase();
    let items = localMemories.filter((memory) => {
      if (!activeScopes.includes(memory.scope)) return false;
      if (!activeTypes.includes(memory.memoryType)) return false;
      if (!queryLower) return true;
      return (
        memory.content.toLowerCase().includes(queryLower) ||
        (memory.category || '').toLowerCase().includes(queryLower)
      );
    });

    items = [...items].sort((a, b) => {
      if (sortBy === 'confidence') {
        return b.confidence - a.confidence;
      }
      if (sortBy === 'scope') {
        return a.scope.localeCompare(b.scope);
      }
      return b.relevanceScore - a.relevanceScore;
    });
    return items;
  }, [memories, activeScopes, activeTypes, localMemories, query, sortBy]);

  if (!memories || memories.length === 0) return null;

  const toggleScope = (scope: MemoryScope) => {
    setActiveScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((value) => value !== scope)
        : [...prev, scope],
    );
  };

  const toggleType = (memoryType: MemoryType) => {
    setActiveTypes((prev) =>
      prev.includes(memoryType)
        ? prev.filter((value) => value !== memoryType)
        : [...prev, memoryType],
    );
  };

  const copyMemory = async (memory: MemoryItem) => {
    try {
      await navigator.clipboard.writeText(memory.content);
    } catch {
      // Ignore clipboard errors; UI remains unchanged.
    }
  };

  const copyAllMemories = async () => {
    const text = filteredMemories
      .map((memory) => `- [${memory.scope}] ${memory.content}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Ignore clipboard errors; UI remains unchanged.
    }
  };

  const exportMemories = () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      memories: filteredMemories,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'memory-export.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const startEdit = (memory: MemoryItem) => {
    setEditingId(memory.id);
    setEditingText(memory.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const saveEdit = async (memory: MemoryItem) => {
    if (!userId) return;
    const trimmed = editingText.trim();
    if (!trimmed) {
      setErrorMessage('Memory content cannot be empty.');
      return;
    }
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await agentApi.upsertMemoryOverride({
        action: 'update',
        memory_id: memory.id,
        content: trimmed,
      });
      // Update local state with new content
      setLocalMemories((prev) =>
        prev.map((item) =>
          item.id === memory.id ? { ...memory, content: trimmed } : item,
        ),
      );
      cancelEdit();
    } catch (error) {
      setErrorMessage((error as Error).message || 'Failed to update memory.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteMemory = async (memory: MemoryItem) => {
    if (!userId) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await agentApi.upsertMemoryOverride({
        action: 'delete',
        memory_id: memory.id,
      });
      setLocalMemories((prev) => prev.filter((item) => item.id !== memory.id));
    } catch (error) {
      setErrorMessage((error as Error).message || 'Failed to delete memory.');
    } finally {
      setIsSaving(false);
    }
  };

  const createMemory = async () => {
    if (!userId) return;
    const trimmed = newContent.trim();
    if (!trimmed) {
      setErrorMessage('Memory content cannot be empty.');
      return;
    }
    setIsSaving(true);
    setErrorMessage(null);
    try {
      // Generate a temporary ID for the create request
      const tempId = `user-${Date.now()}`;
      const response = await agentApi.upsertMemoryOverride({
        action: 'create',
        memory_id: tempId,
        content: trimmed,
      });
      // Create a new MemoryItem for local state
      const newMemory: MemoryItem = {
        id: response.memory_id,
        content: trimmed,
        scope: newScope,
        memoryType: newType,
        confidence: 1,
        source: 'user_explicit',
        relevanceScore: 1,
      };
      setLocalMemories((prev) => [newMemory, ...prev]);
      setNewContent('');
      setShowCreate(false);
    } catch (error) {
      setErrorMessage((error as Error).message || 'Failed to create memory.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors py-0.5"
        title="Retrieved from memory"
      >
        <Database className="w-3 h-3" />
        <span>memory ({localMemories.length})</span>
      </button>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center px-4 py-8">
      <div
        className="absolute inset-0 bg-black/30 cursor-pointer"
        aria-label="Close memory panel"
        onClick={() => setIsExpanded(false)}
        onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(false)}
        role="button"
        tabIndex={0}
      />
      <div
        className="relative z-[10000] w-full max-w-3xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Database className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Memory Control Center
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {localMemories.length} items • {filteredMemories.length} visible
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close memory panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search memories"
              className="flex-1 text-sm border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-100 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
            <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
              <ArrowUpDown className="w-3.5 h-3.5" />
              <select
                value={sortBy}
                onChange={(event) =>
                  setSortBy(
                    event.target.value as 'relevance' | 'confidence' | 'scope',
                  )
                }
                className="text-xs border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="relevance">Relevance</option>
                <option value="confidence">Confidence</option>
                <option value="scope">Scope</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-gray-500">Scope</span>
            {scopedFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => toggleScope(filter.value)}
                className={`px-2 py-1 rounded-full text-xs border ${
                  activeScopes.includes(filter.value)
                    ? 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                }`}
              >
                {filter.label}
              </button>
            ))}
            <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">Type</span>
            {typeFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => toggleType(filter.value)}
                className={`px-2 py-1 rounded-full text-xs border ${
                  activeTypes.includes(filter.value)
                    ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                    : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                }`}
              >
                {filter.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowCreate((prev) => !prev)}
              className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              disabled={!canManage}
            >
              <Plus className="w-3.5 h-3.5" />
              Add memory
            </button>
          </div>

          {showCreate && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2 bg-gray-50 dark:bg-gray-800/50">
              <textarea
                value={newContent}
                onChange={(event) => setNewContent(event.target.value)}
                placeholder="Write a new memory for yourself"
                className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-100 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                rows={3}
                disabled={!canManage || isSaving}
              />
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-gray-400 dark:text-gray-500">Scope</label>
                <select
                  value={newScope}
                  onChange={(event) => setNewScope(event.target.value as MemoryScope)}
                  className="text-xs border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  disabled={!canManage || isSaving}
                >
                  {scopedFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </select>
                <label className="text-xs text-gray-400 dark:text-gray-500">Type</label>
                <select
                  value={newType}
                  onChange={(event) => setNewType(event.target.value as MemoryType)}
                  className="text-xs border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  disabled={!canManage || isSaving}
                >
                  {typeFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={createMemory}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                  disabled={!canManage || isSaving}
                >
                  <Check className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
            </div>
          )}

          {errorMessage ? (
            <div className="text-xs text-red-500">{errorMessage}</div>
          ) : null}
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-4 py-2 bg-gray-50 dark:bg-gray-800/50">
          {filteredMemories.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">
              No memories match your filters.
            </div>
          ) : (
            filteredMemories.map((memory) => (
              <MemoryItemRow
                key={memory.id}
                memory={memory}
                isEditing={editingId === memory.id}
                editingText={editingId === memory.id ? editingText : ''}
                onEditChange={setEditingText}
                onStartEdit={startEdit}
                onCancelEdit={cancelEdit}
                onSaveEdit={saveEdit}
                onDelete={deleteMemory}
                onCopy={copyMemory}
                disabled={!canManage || isSaving}
              />
            ))
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={copyAllMemories}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy visible
          </button>
          <button
            type="button"
            onClick={exportMemories}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <Download className="w-3.5 h-3.5" />
            Export JSON
          </button>
          <div className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <ChevronDown className="w-3.5 h-3.5" />
            Press Esc or click outside to close
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
